<template>
  <CollapsibleSection
    :title="title"
    icon="fas fa-history"
    :context="context"
    :storage-key="storageKey"
    :use-grid="false"
    :count="eventsCount"
  >
    <div class="history-container">
      <!-- Deux natures d'événements, deux onglets : la section ne montre
           qu'une liste à la fois — même anatomie que sur la fiche demande. -->
      <div class="history-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          class="history-tab"
          :class="{ 'is-active': tab === 'calls' }"
          :aria-selected="tab === 'calls'"
          :data-testid="`${context}-history-tab-calls`"
          @click="tab = 'calls'"
        >
          Appels ({{ callsCount }})
        </button>
        <button
          type="button"
          role="tab"
          class="history-tab"
          :class="{ 'is-active': tab === 'emails' }"
          :aria-selected="tab === 'emails'"
          :data-testid="`${context}-history-tab-emails`"
          @click="tab = 'emails'"
        >
          Mails automatiques ({{ emailsCount }})
        </button>

        <InfoButton class="history-info" :title="infoTitle" @click="openInfo" />
      </div>

      <!-- Appels : tous les appels du contact, toutes demandes confondues. -->
      <div v-if="tab === 'calls'" class="history-panel">
        <CallsList
          :calls="calls"
          :loading="loadingCalls"
          :can-add-call="false"
          :can-edit="false"
          :show-quote-link="true"
          :show-title="false"
        />
      </div>

      <!-- Mails automatiques : d'abord ceux qui partiront, puis ceux déjà partis. -->
      <div v-else class="history-panel">
        <!-- Les deux blocs ci-dessous sont gardés par une longueur de tableau : tant que
             le chargement est en cours, ces tableaux sont encore vides et rien ne
             s'afficherait sans ce spinner, pas même celui interne à la liste programmée. -->
        <LoadingSpinner
          v-if="loadingEmails || loadingUpcomingEmails"
          position="fixed"
          message="Chargement des mails..."
        />

        <div v-if="upcomingEmails.length > 0" class="emails-block">
          <DetailLabel tag="h4" class="emails-block-title">Programmés</DetailLabel>
          <UpcomingEmailsListContent
            :emails="upcomingEmails"
            :loading="loadingUpcomingEmails"
            loading-message="Chargement des mails à venir..."
            empty-message="Aucun mail automatique programmé."
            :show-quote-link="true"
            :format-date-fn="formatDateWithDefault"
          />
        </div>

        <div v-if="sentEmails.length > 0" class="emails-block">
          <DetailLabel tag="h4" class="emails-block-title">Déjà envoyés</DetailLabel>
          <!-- Même liste que l'onglet mails de la fiche demande : pastille
               enveloppe, bouton « Aperçu » aligné sur Modifier/Supprimer. -->
          <AutomaticEmailsList
            bare
            :emails="sentEmails"
            :show-recipient="false"
            :show-quote-link="true"
            :context="context"
            storage-key="emails"
          />
        </div>

        <DetailEmpty
          v-if="!loadingEmails && !loadingUpcomingEmails && emailsCount === 0"
          tag="p"
          class="history-empty"
          text="Aucun mail automatique pour ce contact"
        />
      </div>
    </div>

    <InfoModal
      v-model="showCallsInfo"
      title="Appels - Comment ça fonctionne ?"
      size="large"
      @close="showCallsInfo = false"
    >
      <CallsInfo @close="showCallsInfo = false" />
    </InfoModal>

    <InfoModal
      v-model="showAutomaticEmailsInfo"
      title="Mails automatiques - Comment ça fonctionne ?"
      size="large"
      @close="showAutomaticEmailsInfo = false"
    >
      <AutomaticEmailsInfo @close="showAutomaticEmailsInfo = false" />
    </InfoModal>
  </CollapsibleSection>
</template>

<script setup>
import { ref, computed } from 'vue'
import { CollapsibleSection, DetailLabel, DetailEmpty } from '../data'
import { InfoButton, InfoModal, LoadingSpinner } from '../ui'
import CallsList from '../calls/CallsList.vue'
import CallsInfo from '../calls/CallsInfo.vue'
import AutomaticEmailsInfo from './AutomaticEmailsInfo.vue'
import AutomaticEmailsList from './AutomaticEmailsList.vue'
import UpcomingEmailsListContent from './UpcomingEmailsListContent.vue'

const props = defineProps({
  title: {
    type: String,
    default: 'Historique'
  },
  context: {
    type: String,
    default: 'contact'
  },
  storageKey: {
    type: String,
    default: 'historique-info'
  },
  calls: {
    type: Array,
    default: () => []
  },
  loadingCalls: {
    type: Boolean,
    default: false
  },
  sentEmails: {
    type: Array,
    default: () => []
  },
  upcomingEmails: {
    type: Array,
    default: () => []
  },
  loadingEmails: {
    type: Boolean,
    default: false
  },
  loadingUpcomingEmails: {
    type: Boolean,
    default: false
  },
  formatDateWithDefault: {
    type: Function,
    required: true
  }
})

const tab = ref('calls')

const showCallsInfo = ref(false)
const showAutomaticEmailsInfo = ref(false)

const callsCount = computed(() => props.calls?.length || 0)
const emailsCount = computed(
  () => (props.upcomingEmails?.length || 0) + (props.sentEmails?.length || 0)
)

// Compteur affiché dans l'en-tête replié : tout ce qui s'est passé (ou va se
// passer) avec ce contact, appels + mails automatiques.
const eventsCount = computed(() => callsCount.value + emailsCount.value)

// Une seule bulle d'aide, celle de l'onglet ouvert.
const infoTitle = computed(() =>
  tab.value === 'emails'
    ? 'En savoir plus sur les mails automatiques'
    : 'En savoir plus sur les appels'
)

const openInfo = () => {
  if (tab.value === 'emails') showAutomaticEmailsInfo.value = true
  else showCallsInfo.value = true
}
</script>

<style scoped>
/* Les onglets touchent presque leur liste : le filet des tabs suffit à les
   séparer, un grand vide couperait le lien entre l'onglet actif et son
   contenu. */
.history-container {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
}

/* Pas de conteneur de défilement ici : un overflow-x, combiné au margin-bottom
   de -1px des onglets (leur soulignement chevauche le filet), crée un
   micro-scroll vertical parasite. Quand la place manque, les onglets passent
   simplement à la ligne. */
.history-tabs {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0 var(--spacing-4);
  /* Espace réservé à la bulle d'aide ancrée à droite : sans lui, un onglet
     pourrait passer dessous quand la rangée est pleine. */
  padding-right: calc(26px + var(--spacing-2));
  border-bottom: 1px solid var(--color-border);
  margin-bottom: var(--spacing-1);
}

.history-tab {
  white-space: nowrap;
  border: none;
  background: transparent;
  padding: var(--spacing-2) 2px;
  font-family: inherit;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-tertiary);
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: color var(--transition-fast), border-color var(--transition-fast);
}

.history-tab:hover {
  color: var(--color-text-primary);
}

.history-tab.is-active {
  color: var(--color-text-primary);
  border-bottom-color: var(--color-primary);
}

.history-tab:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

/* Hors du flux : dans une rangée qui replie ses onglets, la bulle laissée
   dans le flux se retrouverait seule sur une ligne. */
.history-info {
  position: absolute;
  top: 6px;
  right: 0;
}

.emails-block + .emails-block {
  margin-top: var(--spacing-4);
  padding-top: var(--spacing-4);
  border-top: 1px solid var(--color-border-light);
}

/* Intitulé de sous-bloc : un `DetailLabel`, seul l'espace dessous est local. */
.emails-block .emails-block-title {
  margin-bottom: var(--spacing-2);
}

.history-empty {
  padding: var(--spacing-4) 0;
}
</style>
